/* eslint-disable no-nested-ternary */
import { Box, Link, Typography } from '@mui/material';
import { GalleryIcon } from 'assets/icons';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { FileType, getDocumentIcon, getDocumentTypeName } from 'utils/file-uploader';
import compressFile from 'assets/images/compress-file.png';
import { isYouTubeUrl } from 'utils/youtube-utils';
import { UrlType } from 'pages/media-library/interface';
import StreamingPlayer from '../streaming-player';
import PreviewImage from '../preview-image';
import YouTubePreview from '../youtube-preview';

interface ContentMediaViewProps {
  fileName: string | undefined;
  mediaType: string;
  url?: string;
  thumbnail?: string | undefined;
  isOpenInModal?: boolean;
  fileExtension?: string;
  urlType?: UrlType;
}

const ContentMediaView = ({
  fileName, mediaType, url, thumbnail, isOpenInModal, fileExtension, urlType,
}: ContentMediaViewProps) => {
  const getFileExtension = (): string => {
    if (fileExtension) return fileExtension;
    if (fileName) {
      const parts = fileName.split('.');
      if (parts.length > 1) {
        return `.${parts[parts.length - 1].toLowerCase()}`;
      }
    }
    return '.pdf';
  };

  const documentExtension = getFileExtension();

  const mediaItem = mediaType === FileType.IMAGE ? (
    isOpenInModal
      ? (
        <PreviewImage
          src={url || ''}
          openInModal
          sx={{
            width: 'auto',
            maxWidth: '100%',
            height: 'auto',
            objectFit: 'cover',
            display: 'flex',
            borderRadius: '6px',
            overflow: 'hidden',
          }}
        />
      )
      : (
        <Link href={url} underline="none" target="_blank" sx={{ borderRadius: '6px', overflow: 'hidden', display: 'flex' }}>
          <Box
            component="img"
            src={url}
            sx={{
              width: 'auto',
              maxWidth: '100%',
              height: 'auto',
              objectFit: 'cover',
            }}
          />
        </Link>
      )
  ) : mediaType === FileType.VIDEO ? (
    (urlType === UrlType.YOUTUBE_LINK || isYouTubeUrl(url || '')) ? (
      <YouTubePreview
        url={url || ''}
        height="100%"
        width="100%"
        showControls={isOpenInModal}
        style={{
          borderRadius: '6px',
          overflow: 'hidden',
        }}
      />
    ) : (
      <StreamingPlayer
        src={url || ''}
        poster={thumbnail || undefined}
        openInModal={isOpenInModal}
        sx={{
          height: '100%',
          width: '100%',
          objectFit: 'contain',
          display: 'flex',
          alignItems: 'stretch',
          borderRadius: '6px',
          overflow: 'hidden',
        }}
      />
    )
  ) : mediaType === FileType.DOCUMENT ? (
    <Box
      sx={{
        backgroundColor: '#F2F2F2',
        height: '100%',
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flexDirection: 'column',
      }}
    >
      <Box
        component="img"
        sx={{
          width: '65px',
          height: 'auto',
          objectFit: 'cover',
          margin: '20px',
        }}
        src={documentExtension ? getDocumentIcon(documentExtension) : getDocumentIcon('.pdf')}
      />
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2, textAlign: 'center' }}>
        {fileName}
      </Typography>
      {documentExtension && (
        <Typography variant="caption" sx={{ color: 'text.disabled', textAlign: 'center' }}>
          {getDocumentTypeName(documentExtension)}
        </Typography>
      )}
    </Box>
  ) : mediaType === FileType.COMPRESSED_FILE ? (
    <Box
      sx={{
        backgroundColor: '#F2F2F2',
        height: '100%',
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flexDirection: 'column',
      }}
    >
      <Box
        component="img"
        sx={{
          width: '65px',
          height: 'auto',
          objectFit: 'cover',
          margin: '20px',
        }}
        src={compressFile}
      />
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2, textAlign: 'center' }}>
        {fileName}
      </Typography>
    </Box>
  ) : (
    <Box
      sx={{
        backgroundColor: '#F2F2F2',
        height: '100%',
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flexDirection: 'column',
      }}
    >
      <AttachFileIcon />
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2, textAlign: 'center' }}>
        {fileName}
      </Typography>
    </Box>
  );

  return (
    <div
      data-testid="ContentMediaView"
      style={{
        height: '100%', width: '100%', maxWidth: '100%',
      }}
    >
      {url === '' && (
        <Box
          sx={{
            backgroundColor: '#FAFAFC',
            height: '100%',
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '40px',
          }}
        >
          <GalleryIcon fontSize="large" />
        </Box>
      )}
      {url !== '' && mediaItem}
    </div>
  );
};

ContentMediaView.defaultProps = {
  url: '',
  thumbnail: undefined,
  isOpenInModal: false,
  fileExtension: undefined,
  urlType: undefined,
};

export default ContentMediaView;
